/**
 * Formatting as text edits on the composer's markdown.
 *
 * Every function takes the whole value and the selection and returns the new
 * value and selection, so the toolbar, the keyboard shortcuts and the tests
 * share one implementation and none of them touch the DOM.
 */

export type Edit = { value: string; start: number; end: number };

/**
 * Wraps the selection in `marker`, or unwraps it when it is already wrapped —
 * so pressing Cmd+B twice leaves the text as it was. With nothing selected the
 * caret lands between an empty pair, ready to type into.
 */
export function toggleWrap(value: string, start: number, end: number, marker: string): Edit {
  const m = marker.length;
  const selected = value.slice(start, end);

  // Markers just outside the selection: `**|word|**`.
  if (value.slice(start - m, start) === marker && value.slice(end, end + m) === marker) {
    return {
      value: value.slice(0, start - m) + selected + value.slice(end + m),
      start: start - m,
      end: end - m,
    };
  }
  // Markers inside it: `|**word**|`.
  if (selected.length >= 2 * m && selected.startsWith(marker) && selected.endsWith(marker)) {
    const inner = selected.slice(m, -m);
    return { value: value.slice(0, start) + inner + value.slice(end), start, end: start + inner.length };
  }
  return {
    value: value.slice(0, start) + marker + selected + marker + value.slice(end),
    start: start + m,
    end: end + m,
  };
}

/** A fenced block around the selection, on lines of its own. */
export function wrapCodeBlock(value: string, start: number, end: number): Edit {
  const before = value.slice(0, start);
  const lead = before === "" || before.endsWith("\n") ? "" : "\n";
  const after = value.slice(end);
  const trail = after === "" || after.startsWith("\n") ? "" : "\n";
  const open = `${lead}\`\`\`\n`;
  const selected = value.slice(start, end);
  return {
    value: `${before}${open}${selected}\n\`\`\`${trail}${after}`,
    start: start + open.length,
    end: start + open.length + selected.length,
  };
}

export type LinePrefix = "bullet" | "number" | "quote";

const PREFIX_PATTERN: Record<LinePrefix, RegExp> = {
  bullet: /^[-*] /,
  number: /^\d{1,9}[.)] /,
  quote: /^> ?/,
};

/**
 * Turns every line the selection touches into a list item or a quote, or back
 * into plain lines when they all already are.
 */
export function toggleLinePrefix(value: string, start: number, end: number, kind: LinePrefix): Edit {
  const lineStart = value.lastIndexOf("\n", start - 1) + 1;
  const nextBreak = value.indexOf("\n", end);
  const lineEnd = nextBreak === -1 ? value.length : nextBreak;
  const lines = value.slice(lineStart, lineEnd).split("\n");
  const pattern = PREFIX_PATTERN[kind];
  const all = lines.every((line) => pattern.test(line));

  const next = lines.map((line, i) => {
    if (all) return line.replace(pattern, "");
    // Any other list marker is swapped out rather than stacked.
    const bare = line.replace(PREFIX_PATTERN.bullet, "").replace(PREFIX_PATTERN.number, "");
    if (kind === "bullet") return `- ${bare}`;
    if (kind === "number") return `${i + 1}. ${bare}`;
    return `> ${line}`;
  });
  const replaced = next.join("\n");
  const firstDelta = next[0]!.length - lines[0]!.length;
  return {
    value: value.slice(0, lineStart) + replaced + value.slice(lineEnd),
    start: Math.max(lineStart, start + firstDelta),
    end: end + (replaced.length - (lineEnd - lineStart)),
  };
}

/** A `[text](url)` around the selection, with the url selected to type over. */
export function insertLink(value: string, start: number, end: number): Edit {
  const text = value.slice(start, end) || "link";
  const placeholder = "https://";
  const inserted = `[${text}](${placeholder})`;
  const urlStart = start + text.length + 3;
  return {
    value: value.slice(0, start) + inserted + value.slice(end),
    start: urlStart,
    end: urlStart + placeholder.length,
  };
}

/**
 * Whether the caret is inside an unclosed ``` block, where Enter has to be a
 * newline — a code block is several lines by nature, and sending half of one
 * is never what was meant.
 */
export function insideCodeFence(value: string, caret: number): boolean {
  const before = value.slice(0, caret);
  const fences = before.match(/```/g)?.length ?? 0;
  return fences % 2 === 1;
}

/**
 * Inserts text at the selection, replacing it — an emoji picked, for one.
 */
export function insertAt(value: string, start: number, end: number, text: string): Edit {
  const caret = start + text.length;
  return { value: value.slice(0, start) + text + value.slice(end), start: caret, end: caret };
}

/* ─────────────────────────────── shortcuts ─────────────────────────────── */

export type FormatAction = "bold" | "italic" | "strike" | "code" | "codeBlock" | "link" | "quote" | "bullet" | "number";

type ShortcutEvent = { key: string; code: string; metaKey: boolean; ctrlKey: boolean; shiftKey: boolean; altKey: boolean };

/** The format a key chord asks for, if any. Cmd on a Mac, Ctrl elsewhere. */
export function shortcutFormat(event: ShortcutEvent): FormatAction | null {
  if (!(event.metaKey || event.ctrlKey) || event.altKey) return null;
  const key = event.key.toLowerCase();
  if (!event.shiftKey) {
    if (key === "b") return "bold";
    if (key === "i") return "italic";
    if (key === "e") return "code";
    return null;
  }
  if (key === "x") return "strike";
  // By physical key: Shift+7 is "&" or "/" depending on the layout.
  if (event.code === "Digit7") return "number";
  if (event.code === "Digit8") return "bullet";
  return null;
}
